import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isLoading, user, initializing } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // This will be handled by the Navigate component
  }, [isAuthenticated, user, requiredRole]);

  // Show loading spinner while initializing or checking auth
  if (initializing || isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check if user needs to complete onboarding
  // Skip onboarding check for onboarding-related routes
  const onboardingRoutes = ['/onboarding', '/clinic-setup', '/pet-owner-setup'];
  const isOnboardingRoute = onboardingRoutes.includes(location.pathname);

  if (user && !isOnboardingRoute) {
    // Check if user has completed onboarding based on their role
    const needsOnboarding = !user.hasCompletedOnboarding;

    if (needsOnboarding) {
      // Redirect to appropriate onboarding page
      if (!user.role || user.role === 'GUEST') {
        return <Navigate to="/onboarding" replace />;
      }
      // If role is set but setup not completed, redirect to appropriate setup
      if (user.role === 'CUSTOMER') {
        return <Navigate to="/pet-owner-setup" replace />;
      }
      if (user.role === 'OWNER' || user.role === 'MANAGER' || user.role === 'VETERINARIAN' || user.role === 'STAFF') {
        return <Navigate to="/clinic-setup" replace />;
      }
      // Default to onboarding
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Check if user has required role (if specified)
  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return (
      <div className="access-denied">
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
        <button onClick={() => window.history.back()}>Go Back</button>
      </div>
    );
  }

  return <>{children}</>;
};

// Custom hook to check if user has specific role
export const useHasRole = (requiredRole: string | string[]): boolean => {
  const { user } = useAuth();

  if (!user) return false;

  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  return roles.includes(user.role);
};

// Custom hook to check if user has any of the specified roles
export const useHasAnyRole = (roles: string[]): boolean => {
  return useHasRole(roles);
};

// Custom hook to check if user has all specified roles
export const useHasAllRoles = (roles: string[]): boolean => {
  const { user } = useAuth();

  if (!user) return false;

  return roles.every(role => role === user.role);
};